import * as React from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { ProjectDto } from '../../api/ClientApi';
import { Box, Chip, Collapse, IconButton, Tooltip } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { StyledTableCell } from '../../common/components/styled/StyledTable';
import { createProjectData, currProjectTableRows } from './ProjectsHelper';
import ProjectForm from '../projects/form/ProjectForm';
import { SiteTypeSR } from '../../common/Types';
import { getColorForSiteType } from '../sites/SiteHelper';
import AddSiteToProjectForm from './forms/AddSiteToProjectForm';
import { useAppSelector } from '../../hooks/hooks';
import { canManipulateProjects, canManipulateSites } from '../../redux/selectors/employeeSelector';

type ProjectsTableProps = {
    projects: ProjectDto[] | null;
    projectFilter: string;
    siteFilter: string;
};

export default function ProjectsTable(props: ProjectsTableProps) {
    const { projects, projectFilter, siteFilter } = props;

    const canChangeProject = useAppSelector(canManipulateProjects);
    const canAddSite = useAppSelector(canManipulateSites);

    const [editProjectId, setEditProjectId] = React.useState<number | undefined>(undefined);
    const [openAddSiteFormProjectId, setOpenAddSiteFormProjectId] = React.useState<number | undefined>(undefined);

    const handleOpenProjectsForm = (id?: number) => {
        setEditProjectId(id);
    };

    const handleCloseProjectsForm = () => {
        setEditProjectId(undefined);
    };

    const handleOpenSiteForm = (projectId?: number) => {
        setOpenAddSiteFormProjectId(projectId);
    };

    const handleCloseSiteForm = () => {
        setOpenAddSiteFormProjectId(undefined);
    };

    function Row(props: { row: ReturnType<typeof createProjectData> }) {
        const isFilterActive = projectFilter !== '' || siteFilter !== '';

        const { row } = props;
        const [open, setOpen] = React.useState(isFilterActive);
        return (
            <>
                <TableRow
                    key={row.projectId ?? Math.random()}
                    sx={{ '&:last-child td, &:last-child th': { border: 0 }, bgcolor: open ? '#eee' : '' }}
                >
                    <TableCell>
                        <IconButton aria-label="expand row" size="small" onClick={() => setOpen(!open)}>
                            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                        </IconButton>
                    </TableCell>
                    <TableCell component="th" scope="row">
                        {row.projectName ?? '/'}
                    </TableCell>
                    <TableCell align="center" sx={{ flexGrow: 1 }} />
                    {canChangeProject && (
                        <TableCell align="left" sx={{ width: 50 }}>
                            <Tooltip title="Izmeni projekat" placement="top">
                                <IconButton color="primary" onClick={() => handleOpenProjectsForm(row.projectId)}>
                                    <EditIcon />
                                </IconButton>
                            </Tooltip>
                        </TableCell>
                    )}
                    {canAddSite && (
                        <TableCell align="right" sx={{ width: 50 }}>
                            <Tooltip title="Dodaj skladišta" placement="top">
                                <IconButton color="primary" onClick={() => handleOpenSiteForm(row.projectId)}>
                                    <AddIcon />
                                </IconButton>
                            </Tooltip>
                        </TableCell>
                    )}
                </TableRow>
                <TableRow>
                    <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
                        <Collapse in={open} timeout="auto" unmountOnExit>
                            <Box sx={{ margin: 1 }}>
                                <Table size="small" aria-label="purchases">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>ID</TableCell>
                                            <TableCell align="center">Skladište</TableCell>
                                            <TableCell align="center">Tip</TableCell>
                                            <TableCell align="right">Lokacija</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {row.siteInfo?.map((site) => (
                                            <TableRow key={site.siteId}>
                                                <TableCell component="th" scope="row">
                                                    {site.siteId ?? '/'}
                                                </TableCell>
                                                <TableCell align="center">{site.siteName ?? '/'}</TableCell>
                                                <TableCell align="center">
                                                    {site.siteType ? (
                                                        <Chip
                                                            variant="outlined"
                                                            label={SiteTypeSR[site.siteType]}
                                                            color={getColorForSiteType(site.siteType)}
                                                        />
                                                    ) : (
                                                        '/'
                                                    )}
                                                </TableCell>
                                                <TableCell align="right">{site.address ? site.address : '/'}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </Box>
                        </Collapse>
                    </TableCell>
                </TableRow>
            </>
        );
    }

    return (
        <>
            <TableContainer sx={{ margin: 'auto' }}>
                <Table sx={{ minWidth: 500 }} size={'small'} stickyHeader aria-label="simple table">
                    <TableHead>
                        <TableRow>
                            <StyledTableCell sx={{ width: 70 }} />
                            <StyledTableCell>Projekat</StyledTableCell>
                            <StyledTableCell align="center" sx={{ flexGrow: 1 }} />
                            {(canChangeProject || canAddSite) && (
                                <StyledTableCell
                                    align="center"
                                    colSpan={
                                        canChangeProject && canAddSite ? 2 : canChangeProject || canAddSite ? 1 : 0
                                    }
                                >
                                    Akcije
                                </StyledTableCell>
                            )}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {currProjectTableRows(projects, projectFilter, siteFilter)?.map((row) => {
                            return (
                                <React.Fragment key={Math.random()}>
                                    <Row row={row} />
                                </React.Fragment>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
            <ProjectForm closeForm={handleCloseProjectsForm} open={!!editProjectId} currProjectId={editProjectId} />
            <AddSiteToProjectForm
                open={!!openAddSiteFormProjectId}
                closeForm={handleCloseSiteForm}
                projectId={openAddSiteFormProjectId}
            />
        </>
    );
}
